import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

export const useChatbotStore = defineStore('chatbot', () => {
  const messages = ref([]);
  const isOpen = ref(false);
  const isMinimized = ref(false);
  const isLoading = ref(false);
  const hasNewMessage = ref(false);
  const conversationHistory = ref([]);

  const messageCount = computed(() => messages.value.length);
  const lastMessage = computed(() => messages.value.length ? messages.value[messages.value.length - 1] : null);
  const hasMessages = computed(() => messages.value.length > 0);

  function toggleChat() {
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
      isMinimized.value = false;
      hasNewMessage.value = false;
    }
  }

  function openChat() {
    isOpen.value = true;
    isMinimized.value = false;
    hasNewMessage.value = false;
  }

  function closeChat() {
    isOpen.value = false;
    isMinimized.value = false;
  }

  function minimizeChat() {
    isMinimized.value = true;
  }

  function restoreChat() {
    isMinimized.value = false;
    hasNewMessage.value = false;
  }

  function _getErrorText(lang) {
    return lang === 'fr'
      ? 'Ce service est momentanément indisponible. Veuillez réessayer plus tard.'
      : 'This service is momentarily unavailable. Please try again later.';
  }

  async function sendMessage(text, lang = 'en') {
    if (!text.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: text.trim(),
      timestamp: new Date().toISOString()
    };
    messages.value.push(userMessage);

    conversationHistory.value.push({
      role: 'user',
      content: text.trim()
    });

    isLoading.value = true;

    try {
      const response = await axios.post(`${API_BASE_URL}/api/chatbot/`, {
        question: text.trim(),
        conversation_history: conversationHistory.value.slice(-10),
        lang: lang
      }, {
        headers: {
          'Accept-Language': lang,
          'Content-Type': 'application/json'
        },
        timeout: 90000  // 90 seconds — local LLM needs more time than cloud APIs
      });

      const answer = response.data?.answer || response.data?.response;

      if (answer) {
        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          text: answer,
          timestamp: new Date().toISOString(),
          data: response.data.data || null,
          suggestions: response.data.suggestions || []
        };
        messages.value.push(botMessage);

        conversationHistory.value.push({
          role: 'assistant',
          content: botMessage.text
        });
      } else {
        messages.value.push({
          id: Date.now() + 1,
          type: 'bot',
          text: _getErrorText(lang),
          timestamp: new Date().toISOString(),
          isError: true
        });
      }

      if (isMinimized.value) {
        hasNewMessage.value = true;
      }
    } catch {
      messages.value.push({
        id: Date.now() + 1,
        type: 'bot',
        text: _getErrorText(lang),
        timestamp: new Date().toISOString(),
        isError: true
      });
    } finally {
      isLoading.value = false;
    }
  }

  async function sendSuggestion(suggestion, lang = 'en') {
    await sendMessage(suggestion, lang);
  }

  function clearMessages() {
    messages.value = [];
    conversationHistory.value = [];
  }

  function removeMessage(messageId) {
    const index = messages.value.findIndex(m => m.id === messageId);
    if (index !== -1) messages.value.splice(index, 1);
  }

  function getWelcomeMessage(lang = 'en') {
    return {
      id: 'welcome',
      type: 'bot',
      text: lang === 'fr'
        ? "Bonjour ! Je suis votre assistant répertoire. Comment puis-je vous aider aujourd'hui ?"
        : "Hello! I'm your directory assistant. How can I help you today?",
      timestamp: new Date().toISOString(),
      isWelcome: true
    };
  }

  function initializeChat(lang = 'en') {
    if (messages.value.length === 0) {
      messages.value.push(getWelcomeMessage(lang));
    }
  }

  return {
    messages,
    isOpen,
    isMinimized,
    isLoading,
    hasNewMessage,
    conversationHistory,
    messageCount,
    lastMessage,
    hasMessages,
    toggleChat,
    openChat,
    closeChat,
    minimizeChat,
    restoreChat,
    sendMessage,
    sendSuggestion,
    clearMessages,
    removeMessage,
    initializeChat,
    getWelcomeMessage
  };
});